import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ExternalLink, Check } from "lucide-react";
import { Link } from "@/lib/router-compat";
import heroImage from "@/assets/blog/outsourcing-models-hero.jpg";
import SEOHead from "@/components/SEOHead";
import { BASE_URL } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { useLanguageRouting } from "@/hooks/useLanguageRouting";
import AuthorBio, { AUTHORS } from "@/components/EEAT/AuthorBio";
import ArticleMeta from "@/components/EEAT/ArticleMeta";
import SourcesList from "@/components/EEAT/SourcesList";
import TrustBar from "@/components/EEAT/TrustBar";

const OutsourcingModelsComparison = () => {
  const { t, i18n } = useTranslation(['blog', 'common']);
  const { getLocalizedLink } = useLanguageRouting();
  const currentLang = i18n.language;

  const getArrayTranslation = (key: string): string[] => {
    const result = t(key, { returnObjects: true });
    if (Array.isArray(result)) {
      return result.filter((item): item is string => typeof item === 'string');
    }
    return [];
  };

  const evolviceUrl = currentLang === 'de' ? 'https://www.evolvice.de' : 'https://www.evolvice.de/en';

  const models: Array<{ key: 'outsourcing' | 'nearshoring' | 'offshoring' | 'onshoring'; subKey: 'benefits' | 'advantages' }> = [
    { key: 'outsourcing', subKey: 'benefits' },
    { key: 'nearshoring', subKey: 'advantages' },
    { key: 'offshoring', subKey: 'benefits' },
    { key: 'onshoring', subKey: 'advantages' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn="Nearshoring vs Offshoring vs Onshoring | Guide"
        titleDe="Nearshoring vs Offshoring vs Onshoring | Leitfaden"
        descriptionEn="Compare nearshoring, offshoring, onshoring and outsourcing. Find the right model for your business."
        descriptionDe="Verstehen Sie die Unterschiede zwischen Nearshoring, Offshoring, Onshoring und Outsourcing. Erfahren Sie, welches Modell für Ihr Unternehmen geeignet ist."
        keywords="nearshoring, offshoring, onshoring, outsourcing, software development, global collaboration, Egypt tech hub"
        path="/blog/outsourcing-models-comparison"
        type="article"
        publishedTime="2026-02-14T00:00:00Z"
        modifiedTime="2026-04-19T00:00:00Z"
        author={AUTHORS["sherif-adel"].nameEn}
        article={{
          title: "Nearshoring vs Offshoring vs Onshoring vs Outsourcing",
          description: "Compare nearshoring, offshoring, onshoring and outsourcing models for European companies.",
          url: `${BASE_URL}${currentLang === 'de' ? '' : '/en'}/blog/outsourcing-models-comparison`,
          image: `${BASE_URL}${heroImage}`,
          datePublished: "2026-02-14",
          dateModified: "2026-04-19",
          author: AUTHORS["sherif-adel"].nameEn,
          authorJobTitle: AUTHORS["sherif-adel"].roleEn,
          authorUrl: AUTHORS["sherif-adel"].linkedinUrl,
          reviewer: AUTHORS["evolvice-editorial"].nameEn,
        }}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'Blog', nameDE: 'Blog', url: '/blog' },
          { name: 'Outsourcing Models Comparison', nameDE: 'Outsourcing-Modelle Vergleich', url: '/blog/outsourcing-models-comparison' }
        ]}
      />

      <Navbar />

      <main className="pt-24 pb-32">
        <div className="container mx-auto px-6 max-w-3xl">
          <Button variant="ghost" className="mb-10 group -ml-3" asChild>
            <Link to={getLocalizedLink('/blog')}>
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              {t('blog:backToBlog')}
            </Link>
          </Button>

          <article>
            <header className="mb-12">
              <div className="flex items-center gap-4 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-6">
                <time dateTime="2026-02-14">{t('blog:outsourcingModels.date')}</time>
                <span>·</span>
                <span>{t('blog:outsourcingModels.readTime')} {t('blog:minRead')}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.035em] text-balance mb-6 leading-tight">
                {t('blog:outsourcingModels.title')}
              </h1>

              <ArticleMeta publishedDate="2026-02-14" updatedDate="2026-04-19" />

              <AuthorBio
                authorId="sherif-adel"
                reviewedByDe={AUTHORS["evolvice-editorial"].nameDe}
                reviewedByEn={AUTHORS["evolvice-editorial"].nameEn}
              />

              <div className="rounded-2xl overflow-hidden mb-8 border border-border/60">
                <img src={heroImage} alt="Nearshoring vs Offshoring vs Onshoring comparison" className="w-full h-auto" />
              </div>

              <p className="text-xl text-foreground/80 leading-relaxed">
                {t('blog:outsourcingModels.intro')}
              </p>
            </header>

            <div className="max-w-none">
              <section className="mb-16">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Models</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-8">
                  {t('blog:outsourcingModels.sections.understanding.title')}
                </h2>

                <div className="space-y-4">
                  {models.map(({ key, subKey }) => (
                    <Card key={key}>
                      <CardContent className="p-7">
                        <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3">
                          {t(`blog:outsourcingModels.sections.understanding.${key}.title`)}
                        </h3>
                        <p className="text-foreground/80 leading-relaxed mb-5">
                          {t(`blog:outsourcingModels.sections.understanding.${key}.description`)}
                        </p>

                        <div className="pt-5 border-t border-border/60">
                          <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                            {t(`blog:outsourcingModels.sections.understanding.${key}.${subKey}.title`)}
                          </h4>
                          <ul className="space-y-2">
                            {getArrayTranslation(`blog:outsourcingModels.sections.understanding.${key}.${subKey}.items`).map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-muted-foreground text-sm">
                                <Check className="w-4 h-4 text-foreground/40 mt-0.5 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <section className="mb-16 pt-12 border-t border-border/60">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Spotlight</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-5">
                  {t('blog:outsourcingModels.sections.evolvice.title')}
                </h2>
                <p className="text-foreground/80 leading-relaxed mb-6">
                  {t('blog:outsourcingModels.sections.evolvice.intro')}
                </p>
                <ul className="space-y-3 mb-8">
                  {getArrayTranslation('blog:outsourcingModels.sections.evolvice.points').map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-foreground/80">
                      <Check className="w-4 h-4 text-foreground/40 mt-1 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild>
                  <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                    {t('common:buttons.getStarted')}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </section>

              <SourcesList
                sources={[
                  { publisher: "Deloitte", label: "Global Outsourcing Survey", url: "https://www2.deloitte.com/us/en/insights/focus/business-trends.html", year: 2024 },
                  { publisher: "Stack Overflow", label: "Developer Survey 2024", url: "https://survey.stackoverflow.co/2024/", year: 2024 },
                  { publisher: "Statista", label: "IT outsourcing market by region", url: "https://www.statista.com/outlook/tmo/it-services/it-outsourcing/worldwide", year: 2025 },
                  { publisher: "European Commission", label: "GDPR — international data transfers", url: "https://commission.europa.eu/law/law-topic/data-protection_en", year: 2024 },
                ]}
              />

              <div className="my-12">
                <TrustBar />
              </div>

              <Card className="text-center">
                <CardContent className="py-14 px-8">
                  <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-4">{t('blog:nearshoreGuide.cta.title')}</h2>
                  <p className="text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto">{t('blog:nearshoreGuide.cta.subtitle')}</p>
                  <Button size="lg" asChild>
                    <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                      {t('blog:nearshoreGuide.cta.button')}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OutsourcingModelsComparison;
